import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import teamData from '../../data/teamMembers.json';

gsap.registerPlugin(ScrollTrigger);

const TeamPreview = () => {
    const sectionRef = useRef(null);

    // Flag mapping
    const flags = {
        "KZ": "🇰🇿",
        "IN": "🇮🇳",
        "CA": "🇨🇦"
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.team-card', {
                y: 40,
                opacity: 0,
                duration: 0.5,
                stagger: 0.08,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top 75%',
                    toggleActions: 'play none none reverse',
                },
            });
        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} data-cursor-invert className="relative z-20 px-6 pt-12 pb-16 md:pt-16 md:pb-20">
            <div className="max-w-7xl mx-auto">
                <div className="mb-10 flex flex-col items-start justify-between gap-5 md:mb-12 md:flex-row md:items-end">
                     <div>
                         <span className="home-kicker mb-4 block">The Team</span>
                         <h2 className="text-4xl font-bold text-white mb-3 tracking-tight">Built by students,<br/>driven by curiosity.</h2>
                         <p className="text-gray-400 max-w-lg leading-relaxed">
                            A global team of students from India, Kazakhstan, and Canada, united by the Lodha Genius Programme and a shared fascination with particle physics.
                         </p>
                     </div>
                </div>

                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
                    {teamData.team.map((member, i) => (
                        <Link 
                            key={i} 
                            to="/team" 
                            onClick={() => window.scrollTo(0, 0)}
                            className="team-card group relative block"
                        >
                            <div className="relative aspect-[4/5] overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all duration-300 group-hover:border-blue-500/30">
                                <img 
                                    src={member.photo} 
                                    alt={member.name} 
                                    className="h-full w-full object-cover grayscale opacity-95 brightness-[0.92] transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100 group-hover:brightness-100"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://ui-avatars.com/api/?name=${member.name}&background=1a1a1a&color=fff&bold=true`;
                                    }}
                                />
                                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 via-black/20 to-transparent">
                                     <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-blue-400">{flags[member.country] || "🌐"} {(member.shortRole || member.role).split(' ')[0]}</div>
                                     <h3 className="text-white text-sm font-semibold leading-tight">{member.name.split(' ')[0]}</h3>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TeamPreview;
